import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Chip, Grid, Pagination,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, Link
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArticleIcon from '@mui/icons-material/Article';
import { searchCommitteeReports } from '../services/api';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import RSSButton from './RSSButton';

export default function CommitteeReportsSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [facets, setFacets] = useState({});

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, pageSize = perPage) => {
    const baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    const q = buildQueryWithFilters(baseQuery, filters);

    setLoading(true);
    try {
      const data = await searchCommitteeReports(q, newPage, pageSize);
      setResults(data);
      setPage(newPage);
      if (data.facets) {
        setFacets(data.facets);
      }
      const newParams = { q: baseQuery };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const facetToField = (facetName) => {
    const mapping = {
      'committee_types': 'committee_type',
      'years': 'year'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'committee_types': 'Committee Type',
      'years': 'Year'
    };
    return names[facetName] || facetName;
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, newSize);
  };

  const handleReset = () => {
    setQuery('');
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSearchParams({});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Senate': return 'error';
      case 'House': return 'success';
      case 'Joint': return 'primary';
      default: return 'default';
    }
  };

  const renderFacets = () => {
    if (!facets || Object.keys(facets).length === 0) return null;

    const facetOrder = ['committee_types', 'years'];

    return (
      <Box sx={{ mb: 3 }}>
        {facetOrder.map(facetName => {
          const facetData = facets[facetName];
          if (!facetData || facetData.length === 0) return null;
          const field = facetToField(facetName);

          return (
            <Accordion key={facetName} defaultExpanded={facetName === 'committee_types'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {facetDisplayName(facetName)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List dense sx={{ py: 0 }}>
                  {facetData.slice(0, 20).map((item) => (
                    <ListItem
                      key={item.value}
                      dense
                      sx={{ py: 0.5, cursor: 'pointer' }}
                      onClick={() => handleFacetClick(field, item.value)}
                    >
                      <Checkbox
                        edge="start"
                        checked={activeFilters[field]?.includes(item.value) || false}
                        size="small"
                        sx={{ py: 0 }}
                      />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">{item.value}</Typography>
                            <Chip label={item.count} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  const renderActiveFilters = () => {
    const allFilters = Object.entries(activeFilters).flatMap(([field, values]) =>
      values.map(value => ({ field, value }))
    );

    if (allFilters.length === 0) return null;

    return (
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">Active filters:</Typography>
        {allFilters.map(({ field, value }) => (
          <Chip
            key={`${field}-${value}`}
            label={`${field}: ${value}`}
            size="small"
            onDelete={() => handleFacetClick(field, value)}
          />
        ))}
        <Button size="small" onClick={clearAllFilters}>Clear all</Button>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Committee Reports
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search {results?.total?.toLocaleString() || '1,357'} committee reports from Senate, House of Representatives, and Joint committees.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search committee reports..."
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            size="small"
          />
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            startIcon={<SearchIcon />}
            disabled={loading}
          >
            Search
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>
        </Box>
      </Paper>

      {renderActiveFilters()}

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          {renderFacets()}
        </Grid>

        <Grid item xs={12} md={9}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && results && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Found {results.total?.toLocaleString()} reports
                  {results.took_ms && ` (${results.took_ms}ms)`}
                </Typography>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <RSSButton collection="committee-reports" query={query || '*'} />
              </Box>

              <List>
                {results.hits?.map((report, index) => (
                  <React.Fragment key={report.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        py: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="resultTitle"
                            component={RouterLink}
                            to={`/committee-reports/${encodeURIComponent(report.parlinfo_id)}`}
                            sx={{
                              mb: 0.5,
                              textDecoration: 'none',
                              display: 'block',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {report.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip
                              label={report.committee_type}
                              size="small"
                              color={getTypeColor(report.committee_type)}
                              variant="outlined"
                            />
                            {report.report_date && (
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(report.report_date)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Link
                          component={RouterLink}
                          to={`/committee-reports/${encodeURIComponent(report.parlinfo_id)}`}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
                        >
                          <ArticleIcon fontSize="small" />
                          View
                        </Link>
                        {report.pdf_url && (
                          <Link
                            href={report.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
                          >
                            <PictureAsPdfIcon fontSize="small" />
                            PDF
                          </Link>
                        )}
                        {report.display_url && (
                          <Link
                            href={report.display_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
                          >
                            <OpenInNewIcon fontSize="small" />
                            ParlInfo
                          </Link>
                        )}
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        ID: {report.parlinfo_id}
                      </Typography>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>

              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}

          {!loading && !results && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Enter a search term or click Search to browse all committee reports.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
